import React, { useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { TextLinkBack } from '@nebula/text-link';
import { CrossIcon, InfoIcon } from '@nebula/icons/ui';
import uiIconSizes from '@nebula/icons/ui/iconSizes';
import nebulaTheme from '@nebula/theme';
import { setAutomationElement } from '@experian-uk/corvetteuk-common-ui';

import benefitsDocument from '../../../constants/benefitsDocument';
import { formatDateToReadable } from '../../CoolingOffPeriod/constants';
import EntitlementInfoModal from '../../MySubscription/shared/EntitlementInfoModal';
import type { Feature } from '../../MySubscription/types';
import {
  getFeatureForCancelBenefit,
  getLostBenefits,
  getSubscriptionDisplayName,
  splitBenefitsIntoColumns,
  CancelSubscriptionProduct,
} from './constants';
import {
  BackLinkContainer,
  BackToSubscriptionButton,
  BenefitItem,
  BenefitLabel,
  BenefitsBox,
  BenefitsColumn,
  BenefitsGrid,
  BenefitsSection,
  BenefitsTitle,
  ButtonContainer,
  CardContainer,
  ConfirmButton,
  Description,
  DetailsContainer,
  Divider,
  PageContainer,
  StyledTitleXLarge500,
} from './index.styles';
import NewLoading from '../../CommonComponents/NewLoading';
import ThankYouNonCoolingOffPeriod from './ThankYouNonCoolingOffPeriod';
import AccountDetailsLink from '../../../components/MySubscription/shared/AccountDetailsLink';
import usePageView from '../../../hooks/usePageView';

type BenefitRowProps = {
  label: string;
  onSelect: (label: string) => void;
};

const BenefitRow = ({ label, onSelect }: BenefitRowProps) => (
  <BenefitItem
    onClick={() => onSelect(label)}
    role="button"
    tabIndex={0}
    aria-label={`More info about ${label}`}
    onKeyDown={(e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(label);
      }
    }}
  >
    <CrossIcon iconSize={uiIconSizes.xSmall} iconColor={nebulaTheme.color.messaging.red[70]} aria-hidden="true" />
    <BenefitLabel>{label}</BenefitLabel>
    <InfoIcon iconSize={uiIconSizes.xxSmall} iconColor={nebulaTheme.color.ui.blue[70]} aria-hidden="true" />
  </BenefitItem>
);

type CancelSubscriptionProps = {
  currentSubscription: CancelSubscriptionProduct;
  nextBillingDate?: string | null;
  onBack: () => void;
  onBackHome: () => void;
  onConfirm: () => void;
  isCancelling: boolean;
  pollingComplete: boolean;
  isWebview?: boolean;
};

const PAGE_NAME = 'ProdMoveNonCoolingOffCancellationPage';

export default function CancelSubscriptionNonCoolingOffPeriod({
  currentSubscription,
  nextBillingDate = null,
  onBack,
  onBackHome,
  onConfirm,
  isCancelling,
  pollingComplete,
  isWebview = false,
}: Readonly<CancelSubscriptionProps>) {
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const subscriptionName = getSubscriptionDisplayName(currentSubscription);
  const cancellationTitle = `You are about to cancel your ${subscriptionName} subscription`;
  const benefits = getLostBenefits(currentSubscription);
  const [leftColumn, rightColumn] = splitBenefitsIntoColumns(benefits);
  const formattedNextBillingDate = formatDateToReadable(nextBillingDate);
  const factSheetLink = `${benefitsDocument.baseUrl}/${
    benefitsDocument[currentSubscription as keyof typeof benefitsDocument]
  }`;

  usePageView(PAGE_NAME);

  const handleSelectBenefit = (benefitLabel: string) => {
    const feature = getFeatureForCancelBenefit(benefitLabel);
    if (feature) {
      setSelectedFeature(feature);
    }
  };

  if (isCancelling) {
    return (
      <ThemeProvider theme={nebulaTheme}>
        <NewLoading isWebview={isWebview} />
      </ThemeProvider>
    );
  }

  if (pollingComplete) {
    return (
      <ThankYouNonCoolingOffPeriod
        subscriptionName={subscriptionName}
        formattedNextBillingDate={formattedNextBillingDate}
        onGoHome={onBackHome}
        isWebview={isWebview}
      />
    );
  }

  return (
    <ThemeProvider theme={nebulaTheme}>
      <PageContainer $isWebview={isWebview} data-automation-test-element="cancelSubscription">
        {!isWebview && (
          <BackLinkContainer>
            <TextLinkBack onClick={onBack}>Back</TextLinkBack>
          </BackLinkContainer>
        )}
        <CardContainer $isWebview={isWebview}>
          <DetailsContainer $isWebview={isWebview}>
            <StyledTitleXLarge500 role="text">{cancellationTitle}</StyledTitleXLarge500>
            <Description>
              You&apos;ll move to a free Experian account on {formattedNextBillingDate}. We&apos;ll cancel any future
              subscription payments.
            </Description>
            <BenefitsSection>
              <BenefitsBox>
                <BenefitsTitle>You&apos;ll no longer enjoy all the benefits of {subscriptionName}:</BenefitsTitle>
                <Divider />
                <BenefitsGrid>
                  <BenefitsColumn>
                    {leftColumn.map(benefit => (
                      <BenefitRow key={benefit} label={benefit} onSelect={handleSelectBenefit} />
                    ))}
                  </BenefitsColumn>
                  <BenefitsColumn>
                    {rightColumn.map(benefit => (
                      <BenefitRow key={benefit} label={benefit} onSelect={handleSelectBenefit} />
                    ))}
                  </BenefitsColumn>
                </BenefitsGrid>
                <AccountDetailsLink href={factSheetLink} />
              </BenefitsBox>
            </BenefitsSection>
          </DetailsContainer>
          <ButtonContainer $isWebview={isWebview}>
            <ConfirmButton
              isButton
              buttonKind="primaryMagenta"
              htmlType="button"
              label="Confirm cancellation"
              onClick={onConfirm}
              disabled={isCancelling}
              {...setAutomationElement('confirmButton')}
            />
            <BackToSubscriptionButton
              isButton
              buttonKind="secondaryMagenta"
              htmlType="button"
              label="Back to My subscription"
              onClick={onBack}
              disabled={isCancelling}
              {...setAutomationElement('backToMySubscriptionButton')}
            />
          </ButtonContainer>
        </CardContainer>
        {selectedFeature && <EntitlementInfoModal feature={selectedFeature} onClose={() => setSelectedFeature(null)} />}
      </PageContainer>
    </ThemeProvider>
  );
}
